import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { SERVICES } from "@/data/services";

const fadeUp = {
  hidden: { opacity: 0, scale: 0.96 },
  show: (i = 0) => ({ opacity: 1, scale: 1, transition: { duration: 0.5, delay: i * 0.05 } }),
};

export default function Gallery() {
  const navigate = useNavigate();
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setLightbox(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const items = [
    { src: SERVICES[0].image, label: "Research", span: "lg:col-span-2" },
    { src: "/assets/technology.jpg", label: "Information Technology", span: "" },
    { src: SERVICES[3].image, label: "Consulting", span: "" },
    { src: "/assets/gallery-trading.webp", label: "Trading", span: "lg:col-span-2" },
    { src: "/assets/gallery-agriculture.webp", label: "Agriculture", span: "lg:col-span-2" },
    { src: SERVICES[4].image, label: "Marketing", span: "" },
    { src: SERVICES[5].image, label: "Environmental", span: "" },
    { src: "/assets/gallery-it.png", label: "Information Technology", span: "lg:col-span-2" },
    { src: "/assets/trading-convoy.jpg", label: "Trading", span: "lg:col-span-2" },
    { src: "/assets/agriculture.jpg", label: "Environmental", span: "" },
    { src: "/assets/trading-latra.jpg", label: "Trading", span: "" },
    { src: "/assets/latra-office.jpg", label: "Consulting", span: "lg:col-span-2" },
  ];

  return (
    <div className="bg-brand-grey min-h-screen">
      <SEO
        title="Gallery | Tanz-Bridge International LTD"
        description="Explore our gallery showcasing Tanz-Bridge International LTD's work across Research, IT, Trading, Consulting, Marketing, Agriculture and Environmental services."
        path="/gallery"
      />
      <Navbar onOrder={() => navigate("/#services")} />

      {/* HERO */}
      <section className="relative pt-32 pb-16 overflow-hidden grain bg-brand-dark">
        <div className="absolute inset-0 z-0">
          <img src={SERVICES[1].image} alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-dark via-brand-dark/90 to-brand-dark/50" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
          <motion.p initial="hidden" animate="show" variants={fadeUp} className="text-sm tracking-[0.3em] uppercase font-semibold text-brand-yellow mb-5">
            Gallery
          </motion.p>
          <motion.h1 initial="hidden" animate="show" custom={1} variants={fadeUp}
            className="text-4xl sm:text-5xl lg:text-6xl font-heading font-extrabold tracking-tighter text-white max-w-3xl leading-tight">
            Our work across <span className="text-brand-yellow">six divisions</span>.
          </motion.h1>
        </div>
      </section>

      {/* GALLERY GRID */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 auto-rows-[240px]">
          {items.map((it, i) => (
            <motion.div key={it.src} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fadeUp}
              data-testid={`gallery-item-${i}`}
              onClick={() => setLightbox(it)}
              className={`group relative rounded-lg overflow-hidden border border-gray-200 shadow-sm cursor-pointer ${it.span}`}>
              <img src={it.src} alt={it.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="absolute bottom-4 left-5 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <h3 className="text-lg font-heading text-white">{it.label}</h3>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* LIGHTBOX */}
      {lightbox && (
        <div
          data-testid="gallery-lightbox"
          onClick={() => setLightbox(null)}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <button
            data-testid="gallery-lightbox-close"
            onClick={() => setLightbox(null)}
            aria-label="Close image viewer"
            className="absolute top-5 right-5 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 border border-white/30 flex items-center justify-center text-white transition-colors">
            <X size={22} />
          </button>
          <figure onClick={(e) => e.stopPropagation()} className="max-w-6xl w-full flex flex-col items-center">
            <img
              src={lightbox.src}
              alt={lightbox.label}
              className="max-h-[85vh] w-auto max-w-full object-contain rounded-lg shadow-2xl" />
            <figcaption className="mt-4 text-white font-heading text-lg">{lightbox.label}</figcaption>
          </figure>
        </div>
      )}

      <Footer />
    </div>
  );
}
